'use client';

import { useEffect, useState } from 'react';
import { List, ChevronDown, ChevronRight } from 'lucide-react';

interface TocItem {
  id: string;
  text: string;
  level: number;
  children?: TocItem[];
}

interface TableOfContentsProps {
  contentSelector?: string;
  /** Optional content to render below the TOC in the sticky sidebar (e.g., CTA) */
  stickyFooter?: React.ReactNode;
}

export default function TableOfContents({ contentSelector = 'article', stickyFooter }: TableOfContentsProps) {
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [isOpen, setIsOpen] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    let allHeadings: HTMLHeadingElement[] = [];

    try {
      // Extract headings from the article content
      const article = document.querySelector(contentSelector);
      if (!article) return;

      // Get both H2 and H3 headings
      const h2Elements = Array.from(article.querySelectorAll('h2'));
      const h3Elements = Array.from(article.querySelectorAll('h3'));

      // Build hierarchical structure
      const items: TocItem[] = [];
      let currentH2: TocItem | null = null;

      // Create a map of all headings for easier lookup
      const allHeadingsNodeList = article.querySelectorAll('h2, h3');
      allHeadings = Array.from(allHeadingsNodeList) as HTMLHeadingElement[];

      allHeadings.forEach((heading, index) => {
        const text = heading.textContent || '';

        // Skip empty headings or ones that are too short
        if (!text || text.length < 3) return;

        // Create an ID for the heading if it doesn't have one
        let id = heading.id;
        if (!id) {
          id = `heading-${index}-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
          heading.id = id;
        }

        const level = parseInt(heading.tagName.substring(1));
        const tocItem: TocItem = { id, text, level };

        if (level === 2) {
          // H2 - top level item
          currentH2 = { ...tocItem, children: [] };
          items.push(currentH2);
        } else if (level === 3 && currentH2) {
          // H3 - child of the current H2
          if (!currentH2.children) currentH2.children = [];
          currentH2.children.push(tocItem);
        }
      });

      setTocItems(items);

      // Start with first section expanded
      if (items.length > 0) {
        setExpandedSections(new Set([items[0].id]));
      }

      // Set up intersection observer for active heading
      const observerCallback: IntersectionObserverCallback = (entries) => {
        // Find the entry that's most visible (closest to top of viewport)
        const visibleEntries = entries.filter(entry => entry.isIntersecting);

        if (visibleEntries.length > 0) {
          // Sort by distance from top (smallest boundingClientRect.top wins)
          visibleEntries.sort((a, b) => {
            return a.boundingClientRect.top - b.boundingClientRect.top;
          });

          const activeHeadingId = visibleEntries[0].target.id;
          setActiveId(activeHeadingId);

          // Find which H2 section contains the active heading
          const activeSection = items.find(item =>
            item.id === activeHeadingId ||
            item.children?.some(child => child.id === activeHeadingId)
          );

          // Auto-expand ONLY the active section, collapse all others
          if (activeSection) {
            setExpandedSections(new Set([activeSection.id]));
          }
        }
      };

      const observerOptions: IntersectionObserverInit = {
        rootMargin: '-100px 0px -66% 0px',
        threshold: Array.from({ length: 21 }, (_, i) => i * 0.05), // Every 5% (0, 0.05, 0.10, ..., 1.0)
      };

      observer = new IntersectionObserver(observerCallback, observerOptions);

      allHeadings.forEach((heading) => {
        if (heading.id) {
          observer!.observe(heading);
        }
      });
    } catch (err) {
      console.error('[TableOfContents] Failed to initialize:', err);
    }

    return () => {
      if (observer && allHeadings.length > 0) {
        allHeadings.forEach((heading) => {
          if (heading.id) {
            observer!.unobserve(heading);
          }
        });
      }
    };
  }, [contentSelector]);

  // Auto-scroll the TOC to keep active heading visible
  useEffect(() => {
    if (!activeId) return;

    // Small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      // Find the active button in the desktop TOC
      const activeButton = Array.from(document.querySelectorAll('aside button[data-heading-id]'))
        .find((button) => (button as HTMLElement).dataset.headingId === activeId) as HTMLElement | undefined;
      if (!activeButton) return;

      // Get the nav container (scrollable element)
      const navContainer = activeButton.closest('nav') as HTMLElement;
      if (!navContainer) return;

      // Get positions relative to the scrollable container
      const navScrollTop = navContainer.scrollTop;
      const navClientHeight = navContainer.clientHeight;
      const buttonOffsetTop = activeButton.offsetTop;
      const buttonHeight = activeButton.clientHeight;

      // Calculate if button is outside visible area
      const buttonTop = buttonOffsetTop - navScrollTop;
      const buttonBottom = buttonTop + buttonHeight;

      const isAboveView = buttonTop < 0;
      const isBelowView = buttonBottom > navClientHeight;

      if (isAboveView || isBelowView) {
        // Center the active item in the container
        const targetScroll = buttonOffsetTop - (navClientHeight / 2) + (buttonHeight / 2);

        navContainer.scrollTo({
          top: targetScroll,
          behavior: 'smooth',
        });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [activeId]);

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  if (tocItems.length === 0) {
    return null;
  }

  return (
    <>
      {/* Mobile Toggle Button */}
      <div className="lg:hidden sticky top-20 z-40 mb-8">
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          className="w-full bg-white border-2 border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between hover:border-[#2813e8] transition-colors"
        >
          <span className="flex items-center gap-2 font-semibold text-[#4B4F58]">
            <List className="w-5 h-5" />
            Table of Contents
          </span>
          <span className="text-[#2813e8]">
            {isOpen ? '−' : '+'}
          </span>
        </button>

        {isOpen && (
          <div className="mt-2 bg-white border-2 border-gray-200 rounded-lg p-4 shadow-lg max-h-96 overflow-y-auto">
            <nav>
              <ul className="space-y-1">
                {tocItems.map((item) => {
                  const isExpanded = expandedSections.has(item.id);
                  const hasChildren = item.children && item.children.length > 0;

                  return (
                    <li key={item.id}>
                      <div className="flex items-start">
                        {hasChildren && (
                          <button
                            onClick={() => toggleSection(item.id)}
                            aria-label={isExpanded ? `Collapse ${item.text}` : `Expand ${item.text}`}
                            aria-expanded={isExpanded}
                            className="p-1 mr-1 min-h-[24px] min-w-[24px] inline-flex items-center justify-center hover:bg-gray-100 rounded"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                          </button>
                        )}
                        <button
                          data-heading-id={item.id}
                          onClick={() => {
                            scrollToHeading(item.id);
                            setIsOpen(false);
                          }}
                          className={`text-left flex-1 py-1.5 px-2 rounded transition-colors min-h-[32px] flex items-center ${
                            activeId === item.id
                              ? 'text-[#2813e8] font-semibold bg-blue-50'
                              : 'text-gray-700 hover:text-[#2813e8] hover:bg-gray-50'
                          } ${!hasChildren ? 'ml-5' : ''}`}
                        >
                          {item.text}
                        </button>
                      </div>

                      {hasChildren && isExpanded && (
                        <ul className="ml-9 mt-1 space-y-1">
                          {item.children!.map((child) => (
                            <li key={child.id}>
                              <button
                                data-heading-id={child.id}
                                onClick={() => {
                                  scrollToHeading(child.id);
                                  setIsOpen(false);
                                }}
                                className={`text-left w-full py-1.5 px-2 rounded text-sm transition-colors min-h-[32px] flex items-center ${
                                  activeId === child.id
                                    ? 'text-[#2813e8] font-semibold bg-blue-50'
                                    : 'text-gray-600 hover:text-[#2813e8] hover:bg-gray-50'
                                }`}
                              >
                                {child.text}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Desktop Sticky Sidebar */}
      <aside className="hidden lg:block h-full">
        <div className="sticky top-24 flex flex-col max-h-[calc(100vh-7rem)]">
          {/* CTA at top - always visible but more compact */}
          {stickyFooter && (
            <div className="mb-3 flex-shrink-0">
              {stickyFooter}
            </div>
          )}

          {/* TOC Box - scrollable when content is long, extends closer to bottom */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-md flex-1 min-h-0 flex flex-col">
            <h3 className="text-lg font-bold text-[#4B4F58] mb-4 flex items-center gap-2 flex-shrink-0">
              <List className="w-5 h-5 text-[#2813e8]" />
              Table of Contents
            </h3>
            <nav className="overflow-y-auto flex-1 min-h-0 pr-2">
              <ul className="space-y-1">
                {tocItems.map((item) => {
                  const isExpanded = expandedSections.has(item.id);
                  const hasChildren = item.children && item.children.length > 0;
                  const isActive = activeId === item.id;
                  const hasActiveChild = item.children?.some(child => child.id === activeId);

                  return (
                    <li key={item.id}>
                      <div className="flex items-center">
                        {hasChildren && (
                          <button
                            onClick={() => toggleSection(item.id)}
                            aria-label={isExpanded ? `Collapse ${item.text}` : `Expand ${item.text}`}
                            aria-expanded={isExpanded}
                            className="p-1 mr-1 min-h-[24px] min-w-[24px] inline-flex items-center justify-center hover:bg-gray-100 rounded transition-colors"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                          </button>
                        )}
                        <button
                          data-heading-id={item.id}
                          onClick={() => scrollToHeading(item.id)}
                          className={`text-left flex-1 py-1.5 px-3 rounded transition-all duration-200 min-h-[32px] flex items-center ${
                            isActive
                              ? 'text-[#2813e8] font-semibold bg-blue-50 border-l-4 border-[#2813e8]'
                              : hasActiveChild
                              ? 'text-[#2813e8] font-medium'
                              : 'text-gray-700 hover:text-[#2813e8] hover:bg-gray-50 border-l-4 border-transparent'
                          } ${!hasChildren ? 'ml-5' : ''}`}
                        >
                          {item.text}
                        </button>
                      </div>

                      {hasChildren && isExpanded && (
                        <ul className="ml-10 mt-1 space-y-0.5">
                          {item.children!.map((child) => (
                            <li key={child.id}>
                              <button
                                data-heading-id={child.id}
                                onClick={() => scrollToHeading(child.id)}
                                className={`text-left w-full py-1.5 px-3 rounded text-sm transition-all duration-200 min-h-[32px] flex items-center ${
                                  activeId === child.id
                                    ? 'text-[#2813e8] font-semibold bg-blue-50 border-l-4 border-[#2813e8]'
                                    : 'text-gray-600 hover:text-[#2813e8] hover:bg-gray-50 border-l-4 border-transparent'
                                }`}
                              >
                                {child.text}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Back to Top - inside TOC box at bottom */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="mt-4 w-full bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] text-white py-2 px-4 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex-shrink-0 text-sm"
            >
              ↑ Back to Top
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
